import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { useCallback, useEffect, useState } from "react";
import Navigation from "./Navigation";
import SiteFooter from "./SiteFooter";
import AccessGate from "./AccessGate";
import { Spinner } from "./ui";
import { usePortal } from "../lib/portal";
import { api } from "../lib/api";

// Shared shell for every public page. Guests get the sign-in wall ONLY — the
// whole site stays locked until a student session exists.
export default function PublicLayout() {
  const { data, reload } = usePortal();
  const location = useLocation();
  const navigate = useNavigate();
  const [auth, setAuth] = useState<"checking" | "guest" | "student">("checking");

  const signOut = useCallback(async () => {
    await api.studentSignout();
    setAuth("guest");
  }, []);

  const checkAuth = useCallback(async () => {
    try {
      await api.studentMe();
      setAuth("student");
    } catch {
      setAuth("guest");
    }
  }, []);

  useEffect(() => {
    void checkAuth();
  }, [checkAuth]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  if (auth === "checking") {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Spinner label="Checking sign-in" />
      </div>
    );
  }

  if (auth === "guest") {
    return (
      <AccessGate
        onSignedIn={() => {
          setAuth("student");
          void reload();
          navigate("/", { replace: true });
        }}
      />
    );
  }

  if (!data) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Spinner label="Loading event portal" />
      </div>
    );
  }

  return (
    <div id="top" className="min-h-screen bg-black">
      <Navigation
        wordmark={data.event.wordmark}
        expectedAttendees={data.event.expectedAttendees}
        totalCohort={data.event.totalCohort}
        status={data.event.status}
        onSignOut={() => void signOut()}
      />
      <main>
        <Outlet context={{ reload }} />
      </main>
      <SiteFooter wordmark={data.event.wordmark} />
    </div>
  );
}
